import React from 'react';
import AdminPanelLayout from '@/panel/layouts/AdminPanelLayout';
import Login from '@/panel/pages/Login';
import EventsList from '@/panel/pages/EventsList';
import PanelRoute from '@/panel/components/PanelRoute';
import PanelDashboard from '@/panel/pages/PanelDashboard';
import EventDetails from '@/panel/pages/EventDetails';
import EventCreateWizard from '@/panel/pages/EventCreateWizard';
import ClientsList from '@/panel/pages/ClientsList';
import ComingSoon from '@/panel/pages/ComingSoon';


import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";
import { useBanStatus } from "./hooks/useBanStatus";
import { ThemeProvider } from "@/contexts/ThemeProvider";
import ThemeLoader from "@/components/ThemeLoader";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import BannedUserRoute from "./components/BannedUserRoute";
import AlbumResolution from "./pages/AlbumResolution";
import Inicio from "./pages/Inicio";
import Upload from "./pages/Upload";
import Album from "./pages/album";
import AdminUsers from "./pages/admin/Users";
import Usuario from "./pages/Usuario";
import AdminPanel from "./pages/admin/AdminPanel";
import AdminFotos from "./pages/admin/Fotos";
import AdminConfig from "./pages/admin/Config";
import AdminTheme from "./pages/admin/Theme";
import { AlbumProvider } from "@/contexts/AlbumProvider";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import LosMasVotados from "./pages/LosMasVotados";
import GaleriaEnVivo from "./pages/GaleriaEnVivo";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { AlbumInteractionsProvider } from "@/contexts/AlbumInteractionsContext";
import Mensajes from "./pages/Mensajes";
import AlbumRedirect from "./components/AlbumRedirect";
import AlbumFloatingNav from "./components/AlbumFloatingNav";
import AlbumStatusBar from "./components/AlbumStatusBar";
import * as Sentry from "@sentry/react";
import { useLocation } from "react-router-dom";
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { safeLogRocketTrack } from '@/lib/utils';
import LogRocket from 'logrocket';

const queryClient = new QueryClient();

// Init analytics only in production and not on staging preview hosts
const isProd = process.env.NODE_ENV === 'production';
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isNetlifyPreview = /\.netlify\.app$/.test(hostname);
const isNgrokTunnel = /\.ngrok\.app$/.test(hostname);
const isLovable = /\.lovable\.app$/.test(hostname);
const shouldInitTelemetry = isProd && !(isNetlifyPreview || isLovable || isNgrokTunnel);

if (shouldInitTelemetry) {
  LogRocket.init('ugucxk/bloomkeep-app');
  Sentry.init({
    dsn: "https://f5900abd20d2ba400f89d239be1dd7f3@o4509952911605760.ingest.us.sentry.io/4509952912916480",
    sendDefaultPii: false
  });
}



const RouteScrollReset = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  return null;
};

const AuthenticatedApp = () => {
  useBanStatus();
  const { albumSlug } = useAlbumContext();
  const location = useLocation();

  useEffect(() => {
    safeLogRocketTrack('Página Visitada', {
      url: location.pathname,
      album_name: albumSlug || '',
    });
  }, [location.pathname, albumSlug]);

  return (
    <Routes>
      {/* BloomKeep --> Root album resolution page */}
      <Route path="/" element={<AlbumResolution />} />

      {/* Panel auth-free login route */}
      <Route path='/panel/login' element={<Login />} />

      {/* Protected Panel routes */}
      <Route
        path='/panel'
        element={
          <PanelRoute>
            <AdminPanelLayout />
          </PanelRoute>
        }
      >
        <Route index element={<PanelDashboard />} />
        <Route path='eventos' element={<Navigate to='eventos/listado' replace />} />
        <Route path='eventos/listado' element={<EventsList />} />
        <Route path='eventos/crear' element={<EventCreateWizard />} />
        <Route path='calendario' element={<ComingSoon title='Calendario' />} />
        <Route path='clientes' element={<ClientsList />} />
        <Route path='pagos' element={<ComingSoon title='Pagos' />} />
        <Route path='eventos/:id' element={<EventDetails />} />
      </Route>

      {/* Album-specific routes */}
      <Route path="/:albumSlug/inicio" element={<Inicio />} />
      <Route path="/:albumSlug/login" element={<Auth />} />
      <Route path="/:albumSlug/subir" element={
        <ProtectedRoute>
          <BannedUserRoute>
            <Upload />
          </BannedUserRoute>
        </ProtectedRoute>
      } />
      <Route path="/:albumSlug/album" element={
        <ProtectedRoute>
          <BannedUserRoute>
            <Album />
          </BannedUserRoute>
        </ProtectedRoute>
      } />
      <Route path="/:albumSlug/admin" element={
        <ProtectedRoute>
          <BannedUserRoute>
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          </BannedUserRoute>
        </ProtectedRoute>
      } />
      <Route path="/:albumSlug/admin/usuarios" element={
        <ProtectedRoute>
          <BannedUserRoute>
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          </BannedUserRoute>
        </ProtectedRoute>
      } />
      <Route path="/:albumSlug/admin/fotos" element={
        <ProtectedRoute>
          <BannedUserRoute>
            <AdminRoute>
              <AdminFotos />
            </AdminRoute>
          </BannedUserRoute>
        </ProtectedRoute>
      } />
      <Route path="/:albumSlug/admin/config" element={
        <ProtectedRoute>
          <BannedUserRoute>
            <AdminRoute>
              <AdminConfig />
            </AdminRoute>
          </BannedUserRoute>
        </ProtectedRoute>
      } />
      <Route path="/:albumSlug/admin/theme" element={
        <ProtectedRoute>
          <BannedUserRoute>
            <AdminRoute>
              <AdminTheme />
            </AdminRoute>
          </BannedUserRoute>
        </ProtectedRoute>
      } />
      <Route path="/:albumSlug/usuario" element={
        <ProtectedRoute>
          <BannedUserRoute>
            <Usuario />
          </BannedUserRoute>
        </ProtectedRoute>
      } />
      <Route path="/:albumSlug/los-mas-votados" element={<LosMasVotados />} />
      <Route path="/:albumSlug/galeria-en-vivo" element={<GaleriaEnVivo />} />
      <Route path="/:albumSlug/mensajes" element={<Mensajes />} />

      {/* Album slug redirect - redirects /:albumSlug to /:albumSlug/inicio if album exists */}
      <Route path="/:albumSlug" element={<AlbumRedirect />} />

      {/* Keep share routes as they are (no album prefix) */}
      <Route path="/s" element={<NotFound />} />

      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes >
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AlbumProvider>
        <ThemeProvider>
          <ThemeLoader>
            <AuthProvider>
              <AdminAuthProvider>
                <AlbumInteractionsProvider>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <RouteScrollReset />
                    <AlbumFloatingNav />
                    <AlbumStatusBar />
                    <AuthenticatedApp />
                  </TooltipProvider>
                </AlbumInteractionsProvider>
              </AdminAuthProvider>
            </AuthProvider>
          </ThemeLoader>
        </ThemeProvider>
      </AlbumProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
